import type { Coordinate } from '../models/editor-feature';
import { isUsableScale, type Scale } from './measurement';

/**
 * How a desk is drawn, and how big it really is.
 *
 * The editor has drawn every desk shape as one fixed nine-vertex outline about 0.2 units
 * across — a size that means nothing, because these coordinates are each project's own
 * arbitrary units. On a plan drawn in millimetres that outline is a fifth of a millimetre
 * wide and renders as nothing; on a plan drawn in metres it is a fifth of a metre. Neither is
 * a desk.
 *
 * So a footprint is specified in metres and converted through the plan's scale. On a plan
 * nobody has measured there is no conversion to make, and the legacy outline is used
 * unchanged — which keeps every existing plan behaving exactly as it does today.
 */

export interface DeskFootprint {
  /** Across the front of the desk, in metres. */
  readonly widthMetres: number;
  /** Front to back, in metres. */
  readonly depthMetres: number;
}

/** A catalogue desk: 1.6 m by 0.8 m, which is what the desk detector looks for too. */
export const DEFAULT_FOOTPRINT: DeskFootprint = {
  widthMetres: 1.6,
  depthMetres: 0.8,
};

/**
 * How thick the worktop is, as a fraction of the desk's shorter side.
 *
 * The stored desks on these plans are drawn as the *worktop* — a thin run of surface — rather
 * than as a solid slab, and the C is three of those runs. Taken off the shorter side rather
 * than off each axis separately, so the stroke stays an even thickness instead of being wide
 * along the desk and thin across it.
 */
const WORKTOP_FRACTION = 0.18;

/**
 * The outline the editor has always drawn, normalised around its own centre.
 *
 * Nine vertices, matching every desk shape in the stored data — which is also what the
 * backend's tagger looks for when it decides whether a floor polygon is furniture. Kept as
 * the fallback for an unmeasured plan rather than deleted: changing the shape of every
 * existing plan's furniture is not what setting a scale should mean.
 */
export const LEGACY_TEMPLATE: ReadonlyArray<Coordinate> = [
  [0.0535, -0.0838],
  [-0.0522, -0.0949],
  [-0.0731, 0.104],
  [0.0326, 0.1151],
  [0.0339, 0.1027],
  [-0.0593, 0.0929],
  [-0.0411, -0.0811],
  [0.0522, -0.0713],
  [0.0535, -0.0838],
];

/**
 * A desk's outline, centred on the origin, in drawing units.
 *
 * A C: a worktop running up one side with an arm along the top and the bottom, open on the
 * fourth side where the chair goes. Nine vertices, which is also the vertex count of the desk
 * shapes already on these plans.
 *
 * The outline is a footprint rather than styling — it is what the desk detector measures, what
 * the mobile app fills in one flat colour, and what says how much floor a desk takes.
 *
 * One shape, deliberately. The editor briefly offered a choice of five and then of two, and a
 * choice here only produces floors drawn half one way and half the other.
 *
 * Closed: the first vertex is repeated at the end, as every stored ring is and as the save
 * path expects.
 */
export const symbolRing = (width: number, depth: number): Coordinate[] => {
  if (width <= 0 || depth <= 0) {
    return LEGACY_TEMPLATE.map(([x, y]): Coordinate => [x, y]);
  }

  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const worktop = Math.min(width, depth) * WORKTOP_FRACTION;

  // The back runs along the desk's **longer** side and the two arms along its shorter one.
  // Drawn the other way round — a short back with long arms — the shape reads as a U lying on
  // its side rather than as a C, which is what a desk with two returns actually is.
  //
  // No size guard is needed: the worktop is a fraction of the desk's own shorter side, so the
  // three runs and the opening between them stay in proportion at every size. A desk drawn at
  // 20 cm is a small C, not a self-crossing ring.
  if (width >= depth) {
    // Back along the top, arms reaching toward the front, which is the side the chair goes.
    return [
      [-halfWidth, halfDepth],
      [halfWidth, halfDepth],
      [halfWidth, -halfDepth],
      [halfWidth - worktop, -halfDepth],
      [halfWidth - worktop, halfDepth - worktop],
      [-halfWidth + worktop, halfDepth - worktop],
      [-halfWidth + worktop, -halfDepth],
      [-halfWidth, -halfDepth],
      [-halfWidth, halfDepth],
    ];
  }

  // A desk deeper than it is wide: the same shape stood on end, back down the left-hand side.
  return [
    [-halfWidth, halfDepth],
    [-halfWidth, -halfDepth],
    [halfWidth, -halfDepth],
    [halfWidth, -halfDepth + worktop],
    [-halfWidth + worktop, -halfDepth + worktop],
    [-halfWidth + worktop, halfDepth - worktop],
    [halfWidth, halfDepth - worktop],
    [halfWidth, halfDepth],
    [-halfWidth, halfDepth],
  ];
};

const rotate = (point: Coordinate, degrees: number): Coordinate => {
  if (degrees === 0) {
    return point;
  }

  // Clockwise as the admin sees it, which is the convention the whole editor uses and the
  // one a desk shape's stored rotation is negated into.
  const radians = (-degrees * Math.PI) / 180;
  const [x, y] = point;

  return [
    x * Math.cos(radians) - y * Math.sin(radians),
    x * Math.sin(radians) + y * Math.cos(radians),
  ];
};

/**
 * A desk footprint placed on the plan, in the plan's own units.
 *
 * Falls back to the legacy outline at its legacy size when the plan has no scale. That is
 * not a guess: it is what the editor already draws, and it keeps an unmeasured plan behaving
 * exactly as it does today rather than acquiring desks a thousand times too large.
 */
export const deskShapeRing = (
  at: Coordinate,
  footprint: DeskFootprint,
  scale: Scale,
  rotationDegrees = 0,
): Coordinate[] => {
  const width = isUsableScale(scale) ? footprint.widthMetres * scale : 0;
  const depth = isUsableScale(scale) ? footprint.depthMetres * scale : 0;

  return symbolRing(width, depth).map((point) => {
    const [x, y] = rotate(point, rotationDegrees);
    return [at[0] + x, at[1] + y] as Coordinate;
  });
};

/**
 * How big a drawn outline really is, in metres.
 *
 * Measured after turning the shape back to upright, which is the part that used to be wrong.
 * A rotated rectangle's bounding box is larger than the rectangle — a 1.6 x 0.8 desk at 45
 * degrees measures 1.7 x 1.7 — so reading the box and redrawing at that size grew the desk a
 * little every time anything was changed on it. Turning it back first measures the desk.
 */
export const footprintOf = (
  coordinates: readonly (readonly (readonly Coordinate[])[])[],
  rotationDegrees: number,
  scale: Scale,
): DeskFootprint => {
  const points = coordinates.flat(2);

  if (!points.length || !isUsableScale(scale)) {
    return { widthMetres: 0, depthMetres: 0 };
  }

  const centreX = points.reduce((total, [x]) => total + x, 0) / points.length;
  const centreY = points.reduce((total, [, y]) => total + y, 0) / points.length;

  const upright = points.map(([x, y]) => rotate([x - centreX, y - centreY], -rotationDegrees));
  const xs = upright.map(([x]) => x);
  const ys = upright.map(([, y]) => y);

  return {
    widthMetres: (Math.max(...xs) - Math.min(...xs)) / scale,
    depthMetres: (Math.max(...ys) - Math.min(...ys)) / scale,
  };
};
